import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { PermissionService } from '../../features/security/services/permission.service';
import { EntityType, PermissionAction } from '../../features/security/models/permission.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, of, timeout, catchError } from 'rxjs';

export const permissionGuard: CanActivateFn = async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const router = inject(Router);
    const permissionService = inject(PermissionService);

    const requiredPermission = route.data['requiredPermission'] as { entity: EntityType, action: PermissionAction };

    if (!requiredPermission) {
        return true; // No permission required
    }

    // Re-déclencher si initialisé mais permissions vides (race condition au démarrage)
    const isEmpty = permissionService.initialized() && permissionService.userPermissions().size === 0 && !permissionService.isSuperAdmin();
    if (isEmpty && !permissionService.loading()) {
        try {
            await permissionService.loadPermissions(true);
        } catch (error) {
            // If permissions fail to load, deny access rather than silently granting it
            console.error('[PermissionGuard] Failed to reload permissions — denying access', error);
            return router.parseUrl('/login');
        }
    }

    // Wait for permissions to load if they are currently loading or not initialized
    if (permissionService.loading() || !permissionService.initialized()) {
        console.log(`[PermissionGuard] Waiting for permissions initialization (loading: ${permissionService.loading()}, initialized: ${permissionService.initialized()})...`);

        if (!permissionService.loading() && !permissionService.initialized()) {
            permissionService.loadPermissions();
        }

        try {
            await firstValueFrom(
                toObservable(permissionService.initialized).pipe(
                    filter(initialized => initialized),
                    timeout(8000),
                    catchError(() => {
                        console.error('[PermissionGuard] Timeout waiting for permissions initialization');
                        return of(false);
                    })
                )
            );
        } catch (e) {
            console.error('[PermissionGuard] Error waiting for permissions initialization', e);
            return router.parseUrl('/login');
        }
    }

    // Enforce Company context
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (!selectedCompanyId) {
        if (permissionService.isSuperAdmin()) {
            // Super Admin without company → redirect to company selection
            if (!state.url.includes('/select-company')) {
                console.warn('[PermissionGuard] Super Admin valid but no company context. Redirecting to selection.');
                return router.createUrlTree(['/select-company']);
            }
        } else {
            // Non-super-admin should NEVER reach /select-company
            if (state.url.includes('/select-company')) {
                console.warn('[PermissionGuard] Non-super-admin tried to access /select-company. Redirecting to dashboard.');
                return router.parseUrl('/dashboard');
            }
        }
    }

    // Check for explicit Super Admin requirement
    const requireSuperAdmin = route.data['requireSuperAdmin'] as boolean;
    if (requireSuperAdmin && !permissionService.isSuperAdmin()) {
        console.warn('[PermissionGuard] Super Admin required for this route. Access denied.');
        return router.parseUrl('/dashboard');
    }

    const hasAccess = permissionService.hasPermission(requiredPermission.entity, requiredPermission.action);

    if (hasAccess) {
        return true;
    }

    console.warn(`[PermissionGuard] Access denied for ${state.url}. Required: ${requiredPermission.entity}:${requiredPermission.action}`);

    // Prevent redirect loop: block navigation without redirecting to avoid login ↔ dashboard loop
    if (state.url === '/dashboard') {
        console.error('[PermissionGuard] Access denied to dashboard itself. Blocking navigation to prevent loop.');
        return false;
    }

    // If no access, redirect to dashboard or access denied page
    return router.parseUrl('/dashboard');
};
